import { useState, useCallback, useEffect } from 'react';
import type { ChatSession, CoachingCategory } from '@/types';
import {
  getSessions,
  getSessionsByCategory,
  deleteSession as apiDeleteSession,
  subscribeToSessions,
} from '@/lib/chat-api';

interface UseSessionsOptions {
  filterCategory?: CoachingCategory | null;
}

interface UseSessionsReturn {
  sessions: ChatSession[];
  isLoading: boolean;
  error: string | null;
  selectedCategory: CoachingCategory | null;
  setSelectedCategory: (category: CoachingCategory | null) => void;
  deleteSession: (sessionId: string) => Promise<void>;
  refreshSessions: () => Promise<void>;
}

export function useSessions(options: UseSessionsOptions = {}): UseSessionsReturn {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CoachingCategory | null>(
    options.filterCategory ?? null
  );

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = selectedCategory !== null
        ? await getSessionsByCategory(selectedCategory)
        : await getSessions();
      setSessions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sessions');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory]);

  // Load sessions on mount and when category changes
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Subscribe to realtime session updates
  useEffect(() => {
    const unsubscribe = subscribeToSessions('current-user', (session, eventType) => {
      setSessions((prev) => {
        // If filtering by category, only include matching sessions
        if (selectedCategory !== null && session.category !== selectedCategory) {
          if (eventType === 'DELETE') {
            return prev.filter((s) => s.id !== session.id);
          }
          // For INSERT/UPDATE, remove if category no longer matches
          return prev.filter((s) => s.id !== session.id);
        }

        switch (eventType) {
          case 'INSERT':
            // Add new session at the beginning (most recent)
            if (prev.some((s) => s.id === session.id)) return prev;
            return [session, ...prev];
          case 'UPDATE':
            return prev.map((s) => (s.id === session.id ? session : s));
          case 'DELETE':
            return prev.filter((s) => s.id !== session.id);
          default:
            return prev;
        }
      });
    });

    return unsubscribe;
  }, [selectedCategory]);

  // Delete session
  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      await apiDeleteSession(sessionId);
      // Optimistically remove from state (realtime will also update)
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete session');
      throw err;
    }
  }, []);

  return {
    sessions,
    isLoading,
    error,
    selectedCategory,
    setSelectedCategory,
    deleteSession,
    refreshSessions: fetchSessions,
  };
}
